// Dr.Rx™ Live System & Firestore Telemetry Monitor
import React, { useState, useEffect } from 'react';
import { getErrorLogs, ErrorLog, checkAiServiceHealth } from '../../services/adminService';
import { collection, getCountFromServer, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useToast } from '../../contexts/ToastContext';

const PulseDot: React.FC<{ color: string }> = ({ color }) => (
    <span className="relative flex h-3 w-3">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-75`}></span>
        <span className={`relative inline-flex rounded-full h-3 w-3 ${color}`}></span>
    </span>
);

const MetricCard: React.FC<{ label: string; value: string; subtext: string; status: 'good' | 'warn' | 'bad' }> = ({ label, value, subtext, status }) => {
    const borderColors = {
        good: 'border-emerald-500/30 bg-emerald-950/15',
        warn: 'border-yellow-500/30 bg-yellow-950/15',
        bad: 'border-red-500/30 bg-red-950/15',
    };
    const textColors = {
        good: 'text-emerald-400',
        warn: 'text-yellow-400',
        bad: 'text-red-400',
    };

    return (
        <div className={`p-5 rounded-2xl border ${borderColors[status]} backdrop-blur-sm flex flex-col justify-between h-32 transition-all`}>
            <div className="flex justify-between items-start">
                <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">{label}</span>
                <PulseDot color={status === 'good' ? 'bg-emerald-500' : status === 'warn' ? 'bg-yellow-500' : 'bg-red-500'} />
            </div>
            <div>
                <div className={`text-2xl sm:text-3xl font-black ${textColors[status]} tracking-tight tabular-nums`}>{value}</div>
                <div className="text-xs text-gray-400 mt-1 truncate">{subtext}</div>
            </div>
        </div>
    );
};

interface CollectionCounts {
    nationalAlertItems: number;
    scheduledPearls: number;
    systemLogs: number;
    aiCacheEntries: string;
}

const LiveSystemMonitor: React.FC = () => {
    const { showToast } = useToast();
    const [logs, setLogs] = useState<ErrorLog[]>([]);
    const [aiHealth, setAiHealth] = useState<'Checking...' | 'Operational' | 'Degraded'>('Checking...');
    const [latency, setLatency] = useState<number>(0);
    const [lastCheck, setLastCheck] = useState<Date>(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [counts, setCounts] = useState<CollectionCounts>({
        nationalAlertItems: 0,
        scheduledPearls: 0,
        systemLogs: 0,
        aiCacheEntries: 'Active (6h TTL)',
    });

    const runDiagnostics = async (notify = false) => {
        setIsRefreshing(true);
        const start = Date.now();
        try {
            const [fetchedLogs, isAiOk, pearlsCountSnap, logsCountSnap, nationalDocSnap] = await Promise.all([
                getErrorLogs().catch(() => [] as ErrorLog[]),
                checkAiServiceHealth().catch(() => false),
                getCountFromServer(collection(db, 'clinical_pearls')).catch(() => null),
                getCountFromServer(collection(db, 'system_logs')).catch(() => null),
                getDoc(doc(db, 'system_alerts', 'national')).catch(() => null),
            ]);

            const end = Date.now();
            setLatency(end - start);
            setLogs(fetchedLogs);
            setAiHealth(isAiOk ? 'Operational' : 'Degraded');
            setLastCheck(new Date());

            const nationalAlertsArray = nationalDocSnap?.exists() ? nationalDocSnap.data()?.alerts || [] : [];

            setCounts({
                nationalAlertItems: Array.isArray(nationalAlertsArray) ? nationalAlertsArray.length : 0,
                scheduledPearls: pearlsCountSnap ? pearlsCountSnap.data().count : 0,
                systemLogs: logsCountSnap ? logsCountSnap.data().count : fetchedLogs.length,
                aiCacheEntries: 'Active (6h TTL)',
            });

            if (notify) {
                showToast(`System telemetry refreshed (${end - start}ms).`, 'success', 2000);
            }
        } catch (e) {
            setAiHealth('Degraded');
            if (notify) {
                showToast('Diagnostic check encountered a network error.', 'error');
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        runDiagnostics(false);
        const interval = setInterval(() => runDiagnostics(false), 30000);
        return () => clearInterval(interval);
    }, []);

    const recentErrorsCount = logs.filter(l => {
        const logTime = new Date(l.timestamp).getTime();
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        return logTime > oneDayAgo && l.level === 'ERROR';
    }).length;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#15243E] p-4 rounded-2xl border border-blue-900/50">
                <div>
                    <h3 className="text-lg font-bold text-white">Real-Time Infrastructure & Firestore Telemetry</h3>
                    <p className="text-xs text-gray-400">
                        Live Cloud Function round-trip latency, Gemini clinical proxy health, and Firestore collection counters.
                    </p>
                </div>
                <button
                    onClick={() => runDiagnostics(true)}
                    disabled={isRefreshing}
                    className="px-4 py-2 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-gray-950 font-bold text-xs transition-all shadow-md disabled:opacity-50 self-start sm:self-auto"
                >
                    {isRefreshing ? 'Probing...' : 'Run Live Probe'}
                </button>
            </div>

            {/* Primary Health Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    label="System Status"
                    value={aiHealth === 'Operational' && recentErrorsCount < 5 ? 'HEALTHY' : 'ATTENTION'}
                    subtext={`Last probed: ${lastCheck.toLocaleTimeString()}`}
                    status={aiHealth === 'Operational' && recentErrorsCount < 5 ? 'good' : 'warn'}
                />
                <MetricCard
                    label="Gemini Clinical Engine"
                    value={aiHealth}
                    subtext="Gemini 3.8 Flash Proxy"
                    status={aiHealth === 'Operational' ? 'good' : 'bad'}
                />
                <MetricCard
                    label="Cloud Function Latency"
                    value={`${latency}ms`}
                    subtext="Round-trip RPC + Firestore"
                    status={latency < 800 ? 'good' : latency < 2000 ? 'warn' : 'bad'}
                />
                <MetricCard
                    label="24h Error Volume"
                    value={String(recentErrorsCount)}
                    subtext={`${logs.length} recent logs indexed`}
                    status={recentErrorsCount === 0 ? 'good' : recentErrorsCount < 5 ? 'warn' : 'bad'}
                />
            </div>

            {/* Live Firestore Collection Counters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/50 flex items-center justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block">Live National Alerts</span>
                        <span className="text-xs text-gray-500">system_alerts/national</span>
                    </div>
                    <span className="text-2xl font-black text-white tabular-nums">{counts.nationalAlertItems}</span>
                </div>
                <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/50 flex items-center justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block">Clinical Pearl Docs</span>
                        <span className="text-xs text-gray-500">clinical_pearls collection</span>
                    </div>
                    <span className="text-2xl font-black text-yellow-400 tabular-nums">{counts.scheduledPearls}</span>
                </div>
                <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/50 flex items-center justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block">Total System Logs</span>
                        <span className="text-xs text-gray-500">system_logs collection</span>
                    </div>
                    <span className="text-2xl font-black text-blue-400 tabular-nums">{counts.systemLogs}</span>
                </div>
            </div>

            {/* Live Terminal Stream */}
            <div className="bg-[#0B1324] rounded-2xl border border-blue-900/50 overflow-hidden shadow-2xl">
                <div className="bg-[#15243E] px-6 py-3 border-b border-blue-900/50 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono uppercase text-blue-300 font-bold">Live System Event Stream</span>
                    </div>
                    <span className="text-xs font-mono text-gray-500 tabular-nums">Auto-poll: 30s</span>
                </div>
                <div className="p-4 font-mono text-xs h-72 overflow-y-auto space-y-2 custom-scrollbar">
                    {logs.length === 0 ? (
                        <div className="text-gray-500 text-center py-20">No recent system exceptions logged. All services nominal.</div>
                    ) : (
                        logs.map(log => (
                            <div key={log.id} className="flex gap-3 hover:bg-white/5 p-1.5 rounded transition-colors">
                                <span className="text-gray-500 shrink-0 select-none tabular-nums">
                                    {new Date(log.timestamp).toLocaleTimeString()}
                                </span>
                                <span className={`font-bold shrink-0 w-16 ${log.level === 'ERROR' ? 'text-red-400' : 'text-yellow-400'}`}>
                                    [{log.level}]
                                </span>
                                <span className="text-blue-300 shrink-0 w-36 truncate" title={log.source}>
                                    {log.source}:
                                </span>
                                <span className="text-gray-300 break-all">{log.message}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiveSystemMonitor;
